import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Skeleton,
  Box,
  Typography
} from '@mui/material';
import { Visibility, Edit, Delete } from '@mui/icons-material';
import { useTheme } from '@mui/material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { WorkSchedule } from '../../types/timeclock.types';
import { WORK_SCHEDULE_TYPE_COLORS, WEEKLY_DAYS_OPTIONS } from '../../constants/timeclockConstants';
import { useWorkSchedule } from '../../context/useTimeClock';

interface WorkScheduleTableProps {
  workSchedules: WorkSchedule[];
  isLoading: boolean;
  onView: (workSchedule: WorkSchedule) => void;
}

export function WorkScheduleTable({ workSchedules, isLoading, onView }: WorkScheduleTableProps) {
  const theme = useTheme();
  // const { openDeleteModal, navigateToEdit } = useWorkSchedule();

  const formatTime = (time: string) => {
    return format(new Date(`2000-01-01T${time}`), 'HH:mm');
  };

  const formatDate = (date: string) => {
    return format(new Date(date), 'dd/MM/yyyy', { locale: ptBR });
  };

  const getTypeColor = (type: string) => {
    return WORK_SCHEDULE_TYPE_COLORS[type as keyof typeof WORK_SCHEDULE_TYPE_COLORS] || '#757575';
  };

  const getTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      'REGULAR': 'Regular',
      'FLEXIBLE': 'Flexível',
      'PART_TIME': 'Meio Período',
      'OVERTIME': 'Horas Extras'
    };
    return typeMap[type] || type;
  };

  const formatWeeklyDays = (days: string[]) => {
    if (!days || days.length === 0) return '-';
    
    const dayLabels = WEEKLY_DAYS_OPTIONS
      .filter(option => days.includes(option.value))
      .map(option => option.label);
    
    return dayLabels.join(', ');
  };

  if (isLoading) {
    return (
      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: `1px solid ${theme.palette.divider}` }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome da Jornada</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Início</TableCell>
              <TableCell>Fim</TableCell>
              <TableCell>Dias da Semana</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...Array(5)].map((_, index) => (
              <TableRow key={index}>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (workSchedules.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          backgroundColor: theme.palette.background.paper
        }}
      >
        <Typography variant="h6" color={theme.palette.text.secondary} gutterBottom>
          Nenhuma jornada encontrada
        </Typography>
        <Typography variant="body2" color={theme.palette.text.secondary}>
          Comece criando uma nova jornada de trabalho
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: `1px solid ${theme.palette.divider}` }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Nome da Jornada</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell>Início</TableCell>
            <TableCell>Fim</TableCell>
            <TableCell>Dias da Semana</TableCell>
            <TableCell align="center">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {workSchedules.map((workSchedule) => (
            <TableRow key={workSchedule.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {workSchedule.name}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={getTypeLabel(workSchedule.type)}
                  size="small"
                  sx={{
                    backgroundColor: getTypeColor(workSchedule.type),
                    color: 'white',
                    fontWeight: 500
                  }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatTime(workSchedule.startTime)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatTime(workSchedule.endTime)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatWeeklyDays(workSchedule.weeklyDays)}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <IconButton
                    size="small"
                    onClick={() => onView(workSchedule)}
                    sx={{ color: theme.palette.primary.main }}
                  >
                    <Visibility fontSize="small" />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
} 